import React from 'react';
import { useProgress } from '../contexts/ProgressContext';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Link } from 'react-router-dom';

const Certificate = () => {
  const {
    progress,
    calculateAverageScore,
    calculateCompletionPercentage,
    isCertificateEligible
  } = useProgress();
  const { user } = useKindeAuth();

  const eligible = isCertificateEligible();
  const avgScore = calculateAverageScore();
  const completion = calculateCompletionPercentage();

  const displayName = progress.userName || user?.given_name || user?.givenName || 'Student';
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-navy to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </Link>
          {eligible && (
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-teal to-accent text-white font-semibold hover:shadow-lg transition-all print:hidden"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
              </svg>
              Print / Save as PDF
            </button>
          )}
        </div>

        {/* Certificate Card */}
        <div
          id="certificate"
          className="bg-white rounded-2xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none"
        >
          {/* Decorative border */}
          <div className="p-2 sm:p-3 bg-gradient-to-r from-teal via-accent to-purple-500">
            <div className="bg-white border-4 border-double border-teal/40 rounded-xl p-6 sm:p-10 md:p-14 text-center relative">
              {/* Corner ornaments */}
              <div className="absolute top-4 left-4 w-10 h-10 border-t-4 border-l-4 border-teal/60 rounded-tl-lg hidden sm:block" />
              <div className="absolute top-4 right-4 w-10 h-10 border-t-4 border-r-4 border-teal/60 rounded-tr-lg hidden sm:block" />
              <div className="absolute bottom-4 left-4 w-10 h-10 border-b-4 border-l-4 border-teal/60 rounded-bl-lg hidden sm:block" />
              <div className="absolute bottom-4 right-4 w-10 h-10 border-b-4 border-r-4 border-teal/60 rounded-br-lg hidden sm:block" />

              {/* Header */}
              <div className="mb-8">
                <div className="text-teal text-sm font-semibold uppercase tracking-[0.3em] mb-2">
                  30-Day Stock Market Course
                </div>
                <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-2 tracking-tight">
                  Certificate of Completion
                </h1>
                <div className="w-24 h-1 bg-gradient-to-r from-teal to-accent mx-auto rounded-full" />
              </div>

              {/* Body */}
              <p className="text-gray-600 text-lg mb-2">This certificate is proudly presented to</p>
              <div className="my-6">
                <div className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2 font-serif">
                  {displayName}
                </div>
                <div className="w-48 h-0.5 bg-gray-300 mx-auto" />
                <p className="text-gray-400 text-sm mt-2">for successfully completing all 30 days of the course</p>
              </div>

              {/* Stats */}
              <div className="flex justify-center gap-8 sm:gap-16 my-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal">{completion}%</div>
                  <div className="text-gray-500 text-sm">Course Completion</div>
                </div>
                <div className="w-px bg-gray-300" />
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent">{avgScore}%</div>
                  <div className="text-gray-500 text-sm">Average Quiz Score</div>
                </div>
              </div>

              {/* Date + Signature */}
              <div className="flex justify-between items-end mt-10 print:mt-14">
                <div className="text-left">
                  <div className="text-gray-400 text-sm mb-1">Date</div>
                  <div className="w-40 h-0.5 bg-gray-300" />
                  <div className="text-gray-700 font-medium mt-1">{today}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 text-sm mb-1">Course Instructor</div>
                  <div className="w-40 h-0.5 bg-gray-300" />
                  <div className="text-gray-700 font-medium mt-1">Stock Market Course Team</div>
                </div>
              </div>

              {/* Watermark */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04] print:opacity-[0.06]">
                <span className="text-[10rem] sm:text-[16rem] font-extrabold text-gray-900 select-none">📈</span>
              </div>
            </div>
          </div>
        </div>

        {/* Not eligible message */}
        {!eligible && (
          <div className="mt-6 bg-yellow-900/30 border border-yellow-500 rounded-xl p-6 text-center">
            <p className="text-yellow-300 font-semibold text-lg mb-2">
              🔒 Certificate Locked
            </p>
            <p className="text-gray-300">
              Complete all 30 chapters and quizzes with an average score of 60% or higher to unlock your certificate.
            </p>
            <Link
              to="/"
              className="inline-block mt-4 px-6 py-2.5 bg-gradient-to-r from-teal to-accent text-white font-semibold rounded-lg hover:shadow-lg transition-all"
            >
              Go to Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificate;
